import { Spin } from "antd";
import i18n from "i18next";
import type { FC } from "react";
import { useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";

import { FormManager } from "../../../../../components/Form/FormManager";
import { useLoadFormDefinitionQuery } from "../../../../../components/Form/services/server/serverApi";
import { FormLayoutType } from "../../../../../components/Form/types";
import type { FormProps } from "../../../../../components/Form/types/FormProps";
import { ResourceName } from "../../../../../core/router/config/ResourceName";
import { BASE_PATH } from "../../../../../core/router/types/routerTypes";
import { showSuccessNotification } from "../../../../../core/setup/redux/state/notifications/notificationsState";
import { useEmvKernelProfileSaveMutation } from "../../services/server/EmvKernelProfileApi";
import type { EmvKernelProfileEntity } from "../../types/EmvKernelProfileEntity";

export const EmvKernelProfileCreateForm: FC<FormProps> = ({ runSubmit }) => {
    const { data: formDefinition, isFetching: configIsFetching } = useLoadFormDefinitionQuery("emv-kernel-profile-eft4f");

    const [formData, setFormData] = useState<EmvKernelProfileEntity>({} as EmvKernelProfileEntity);
    const onChangeCallback = useCallback(setFormData, [setFormData]);

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [saveFormData, { isLoading: saveFormIsInProgress }] = useEmvKernelProfileSaveMutation();
    const onValidatedCallback = useCallback(
        async (valid: boolean) => {
            if (!valid || !formData) {
                return;
            }

            const result = await saveFormData({ data: formData });

            const hasError = "error" in result;
            if (!hasError) {
                dispatch(
                    showSuccessNotification({
                        text: i18n.t("common.formSaveSuccess", {
                            id: result.data.id,
                            resource: "EmvKernelProfile",
                        }),
                        autoCloseDisabled: false,
                    }),
                );
                navigate(`${BASE_PATH}/${ResourceName.EMV_KERNEL_PROFILES}`);
            }
        },
        [saveFormData, formData, navigate, dispatch],
    );

    return (
        <Spin spinning={saveFormIsInProgress || configIsFetching}>
            {formDefinition?.formConfiguration && (
                <FormManager<EmvKernelProfileEntity>
                    name="EmvKernelProfileCreateForm"
                    formData={formData}
                    formLayout={FormLayoutType.TABS}
                    formDefinition={formDefinition.formConfiguration}
                    onChange={onChangeCallback}
                    shouldValidate={runSubmit}
                    onValidated={onValidatedCallback}
                />
            )}
        </Spin>
    );
};
